import React, { createContext, useContext } from 'react';
import {
  useRiskSocket,
  RiskSocketState,
  RiskFinalResult,
  RiskQuestion,
} from '../hooks/useRiskSocket';

// ── Types ──────────────────────────────────────────────────────────────────────

interface RiskContextValue {
  state: RiskSocketState;
  start: (companyId: string, policyIds: string[], token: string, jurisdiction?: string) => void;
  resume: (sessionId: string, token: string) => void;
  reset: () => void;
  disconnect: () => void;
  setQuestions: (questions: RiskQuestion[]) => void;
  setFinalResult: (result: RiskFinalResult) => void;
  resumePolling: (sessionId?: string) => boolean;
  retryNow: (sessionId: string) => Promise<void>;
}

// ── Context ────────────────────────────────────────────────────────────────────

const RiskContext = createContext<RiskContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

/**
 * Wraps the app at the root level so RiskStartPage state persists
 * across tab navigation (component unmount / remount) while a risk
 * analysis is in progress.
 */
export function RiskProvider({ children }: { children: React.ReactNode }) {
  const socket = useRiskSocket();
  return (
    <RiskContext.Provider value={socket}>
      {children}
    </RiskContext.Provider>
  );
}

// ── Consumer hook ──────────────────────────────────────────────────────────────

export function useRisk(): RiskContextValue {
  const ctx = useContext(RiskContext);
  if (!ctx) throw new Error('useRisk must be used inside <RiskProvider>');
  return ctx;
}
